/**
 * Brand Navigation - Floating Brutalist Bar
 * 
 * Collapsed: [CATEGORIES]  [LOGO]  [CART]
 * Expanded:  [BRAND SWITCHER]  [LOGO]  [CART]
 *            [GRID CONTENT]
 * 
 * Behavior:
 * - Hover any element in collapsed state → expand
 * - Hover brand name in expanded state → switch brand color/content/logo
 * - Mouse leave → collapse back to current page's brand
 * - Click navigates immediately
 */

(function() {
  'use strict';

  const BRAND_LOGOS = {
    corp: 'Remilia Corporation',
    milady: 'Milady Gift Shop',
    atelier: 'Remilia Atelier'
  };

  const BRAND_URLS = {
    corp: '/pages/corp',
    milady: '/pages/milady',
    atelier: '/pages/atelier'
  };

  const SELECTORS = {
    nav: '[data-brand-nav]',
    bar: '[data-brand-bar]',
    dropdown: '[data-brand-dropdown]',
    navTrigger: '[data-nav-trigger]',
    brandTrigger: '[data-brand-trigger]',
    brandContent: '[data-brand-content]',
    brandCategories: '[data-brand-categories]',
    brandLogo: '[data-brand-logo]',
    brandLogoExpanded: '[data-brand-logo-expanded]',
    brandName: '[data-brand-name]',
    brandBreadcrumb: '[data-brand-breadcrumb]',
    mobileToggle: '[data-mobile-nav-toggle]',
    mobileOverlay: '[data-mobile-nav-overlay]'
  };

  const CLASSES = {
    expanded: 'brand-nav--expanded',
    contentVisible: 'brand-nav__content--visible',
    mobileOpen: 'brand-nav__mobile-overlay--open',
    bodyNoScroll: 'no-scroll'
  };

  let currentPageBrand = 'corp';
  let activeBrand = 'corp';
  let isExpanded = false;
  let collapseTimeout = null;

  /**
   * Initialize
   */
  function init() {
    const nav = document.querySelector(SELECTORS.nav);
    if (!nav) return;

    currentPageBrand = nav.dataset.currentBrand || 'corp';
    activeBrand = currentPageBrand;

    setupDesktopNav(nav);
    setupMobileNav();
  }

  /**
   * Setup desktop nav
   */
  function setupDesktopNav(nav) {
    const bar = nav.querySelector(SELECTORS.bar);
    const dropdown = nav.querySelector(SELECTORS.dropdown);
    
    // Hover anywhere on bar - expand nav
    bar.addEventListener('mouseenter', () => {
      clearTimeout(collapseTimeout);
      expand(nav, currentPageBrand);
    });

    // Brand triggers in expanded state - switch brand
    const brandTriggers = nav.querySelectorAll(SELECTORS.brandTrigger);
    brandTriggers.forEach(trigger => {
      trigger.addEventListener('mouseenter', () => {
        clearTimeout(collapseTimeout);
        const brand = trigger.dataset.brandTrigger;
        switchBrand(nav, brand);
      });

      trigger.addEventListener('click', () => {
        const brand = trigger.dataset.brandTrigger;
        window.location.href = BRAND_URLS[brand];
      });
    });

    if (dropdown) {
      dropdown.addEventListener('mouseenter', () => {
        clearTimeout(collapseTimeout);
      });

      dropdown.addEventListener('mouseleave', () => {
        collapseTimeout = setTimeout(() => {
          collapse(nav);
        }, 150);
      });
    }

    // Collapse when leaving bar (but give time to reach dropdown)
    bar.addEventListener('mouseleave', () => {
      collapseTimeout = setTimeout(() => {
        collapse(nav);
      }, 150);
    });
  }

  /**
   * Expand the nav bar
   */
  function expand(nav, brand) {
    if (isExpanded) return;

    nav.classList.add(CLASSES.expanded);
    isExpanded = true;

    // Show the brand's content
    showBrandContent(nav, brand);
    updateLogo(nav, brand);
    updateBarColor(nav, brand);
    // Don't update brand name/breadcrumb - preserve page context
    activeBrand = brand;
  }

  /**
   * Collapse the nav bar
   */
  function collapse(nav) {
    nav.classList.remove(CLASSES.expanded);
    isExpanded = false;

    // Hide all content
    const allContent = nav.querySelectorAll(SELECTORS.brandContent);
    allContent.forEach(content => {
      content.classList.remove(CLASSES.contentVisible);
    });

    // Reset color to current page's brand
    updateBarColor(nav, currentPageBrand);
    activeBrand = currentPageBrand;
  }

  /**
   * Switch to different brand (while expanded)
   */
  function switchBrand(nav, brand) {
    if (activeBrand === brand) return;

    nav.classList.add('brand-nav--switching');
    showBrandContent(nav, brand);
    updateLogo(nav, brand);
    updateBarColor(nav, brand);
    // Don't update brand name/breadcrumb - preserve page context
    activeBrand = brand;

    // Remove after transition completes so initial hover stays instant
    setTimeout(function() { nav.classList.remove('brand-nav--switching'); }, 150);
  }

  /**
   * Show specific brand's content
   */
  function showBrandContent(nav, brand) {
    // Hide all content
    const allContent = nav.querySelectorAll(SELECTORS.brandContent);
    allContent.forEach(content => {
      content.classList.remove(CLASSES.contentVisible);
    });

    // Show target brand's content
    const targetContent = nav.querySelector(`[data-brand-content="${brand}"]`);
    if (targetContent) {
      targetContent.classList.add(CLASSES.contentVisible);
    }
  }

  /**
   * Update logo text and href
   */
  function updateLogo(nav, brand) {
    const logoExpanded = nav.querySelector(SELECTORS.brandLogoExpanded);
    if (logoExpanded) {
      logoExpanded.textContent = BRAND_LOGOS[brand];
      logoExpanded.href = BRAND_URLS[brand];
    }
  }

  /**
   * Update bar color class
   */
  function updateBarColor(nav, brand) {
    nav.classList.remove('brand-nav--corp', 'brand-nav--milady', 'brand-nav--atelier');
    nav.classList.add(`brand-nav--${brand}`);
  }



  /**
   * Setup mobile nav
   */
  function setupMobileNav() {
    const toggle = document.querySelector(SELECTORS.mobileToggle);
    const overlay = document.querySelector(SELECTORS.mobileOverlay);

    if (toggle) {
      toggle.addEventListener('click', () => {
        if (overlay && overlay.classList.contains(CLASSES.mobileOpen)) {
          closeMobileNav();
        } else {
          openMobileNav();
        }
      });
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeMobileNav();
    });

    // Close mobile nav when resizing past the mobile breakpoint
    const mobileBreakpoint = window.matchMedia('(min-width: 769px)');
    mobileBreakpoint.addEventListener('change', (e) => {
      if (e.matches) closeMobileNav();
    });
  }

  /**
   * Open mobile nav
   */
  function openMobileNav() {
    // Close mobile bag first if open
    if (window.REMILIA && window.REMILIA.cart) {
      window.REMILIA.cart.closeMobileBag();
    }
    const overlay = document.querySelector(SELECTORS.mobileOverlay);
    const toggle = document.querySelector(SELECTORS.mobileToggle);
    const bar = document.querySelector('.brand-nav__mobile-bar');
    if (overlay) {
      overlay.classList.add(CLASSES.mobileOpen);
      document.body.classList.add(CLASSES.bodyNoScroll);
      if (toggle) toggle.textContent = 'CLOSE 关';
      if (bar) bar.classList.add('brand-nav__mobile-bar--solid');
    }
  }

  /**
   * Close mobile nav
   */
  function closeMobileNav() {
    const overlay = document.querySelector(SELECTORS.mobileOverlay);
    const toggle = document.querySelector(SELECTORS.mobileToggle);
    const bar = document.querySelector('.brand-nav__mobile-bar');
    if (overlay) {
      overlay.classList.remove(CLASSES.mobileOpen);
      document.body.classList.remove(CLASSES.bodyNoScroll);
      if (toggle) toggle.textContent = 'MENU 目';
      if (bar) bar.classList.remove('brand-nav__mobile-bar--solid');
    }
  }

  // Init on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for debugging
  window.REMILIA = window.REMILIA || {};
  window.REMILIA.nav = {
    expand,
    collapse,
    switchBrand,
    openMobileNav,
    closeMobileNav
  };
})();
